import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Clock, CheckCircle } from '@phosphor-icons/react';
import { PrimarySectionBackdrop } from '../components/PrimarySectionBackdrop';
import { Seo } from '../components/Seo';
import { SEO } from '../constants/seoCopy';
import { API_BASE } from '../config';

const cards = [
  {
    to: '/projekti/u-toku',
    title: 'Projekti u toku',
    description:
      'Aktivna gradilišta i radovi u realizaciji — pratite šta trenutno radimo.',
    icon: Clock,
    iconBg: 'bg-white/20 ring-white/30',
    borderHover: 'hover:border-cool/50',
    finishedFlag: false,
  },
  {
    to: '/projekti/zavrseni',
    title: 'Završeni projekti',
    description:
      'Realizovani objekti i reference — klimatizacija, grejanje, ventilacija.',
    icon: CheckCircle,
    iconBg: 'bg-white/20 ring-white/30',
    borderHover: 'hover:border-mint/50',
    finishedFlag: true,
  },
];

const FIXED_CARD_COVERS = {
  false: '/projekti/notfinished-cover.png',
  true: '/projekti/finished-cover.png',
};

export default function ProjektiPage() {
  const [previewByFinished, setPreviewByFinished] = useState({
    false: null,
    true: null,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetch(`${API_BASE}/api/gallery`, { cache: 'no-store' })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((data) => {
        if (cancelled || !Array.isArray(data)) return;
        const inProgress = data.filter((item) => item.finished === false);
        const finished = data.filter((item) => item.finished === true);
        setPreviewByFinished({
          false: inProgress[0] ?? null,
          true: finished[0] ?? null,
        });
      })
      .catch(() => {
        if (!cancelled) setPreviewByFinished({ false: null, true: null });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div data-testid="projekti-page" className="pt-20">
      <Seo {...SEO.projekti} />

      <section className="relative py-20 sm:py-24 bg-primary overflow-hidden" data-testid="projekti-header">
        <PrimarySectionBackdrop />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55 }}
            className="max-w-2xl"
          >
            <span className="inline-block text-cool font-semibold uppercase tracking-[0.2em] text-sm mb-4">
              Naši radovi
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white mb-5">
              Projekti
            </h1>
            <p className="normal-case text-white/70 text-lg leading-relaxed">
              Izaberite da li želite da vidite aktivne projekte ili završene reference.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-16 sm:py-24 bg-white" data-testid="projekti-choice">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-7 md:gap-10">
            {cards.map((card, index) => {
              const Icon = card.icon;
              const preview = previewByFinished[card.finishedFlag];
              const fixedCover = FIXED_CARD_COVERS[String(card.finishedFlag)];
              const coverUrl = fixedCover || (preview ? `${API_BASE}${preview.src}` : null);
              const coverAlt = preview?.alt?.trim() ? preview.alt : card.title;

              return (
                <motion.div
                  key={card.to}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.45, delay: 0.08 + index * 0.1 }}
                >
                  <Link
                    to={card.to}
                    className={`group relative flex min-h-[360px] sm:min-h-[420px] overflow-hidden rounded-2xl border-2 border-primary/15 shadow-md transition-all duration-300 hover:shadow-2xl hover:-translate-y-0.5 ${card.borderHover}`}
                    data-testid={`projekti-card-${card.to.replace(/^\//, '').replace(/\//g, '-')}`}
                  >
                    {loading && (
                      <div
                        className="absolute inset-0 bg-gradient-to-br from-primary/50 to-primary/20 animate-pulse"
                        aria-hidden
                      />
                    )}
                    {!loading && coverUrl && (
                      <img
                        src={coverUrl}
                        alt={coverAlt}
                        className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    )}
                    {!loading && !coverUrl && (
                      <div
                        className="absolute inset-0 bg-gradient-to-br from-primary via-primary/85 to-cool/70"
                        aria-hidden
                      />
                    )}
                    <div
                      className="absolute inset-0 bg-gradient-to-t from-primary via-primary/75 to-primary/45"
                      aria-hidden
                    />
                    <div className="relative z-10 flex h-full flex-col justify-end p-8 sm:p-10 lg:p-12">
                      <div
                        className={`mb-6 inline-flex rounded-2xl p-4 ring-1 backdrop-blur-sm ${card.iconBg}`}
                      >
                        <Icon className={`h-10 w-10 text-white`} weight="duotone" aria-hidden />
                      </div>
                      <h2 className="font-heading mb-3 text-3xl font-extrabold text-white sm:text-4xl">
                        {card.title}
                      </h2>
                      <p className="mb-8 leading-relaxed text-white/85">{card.description}</p>
                      <span className="inline-flex w-fit items-center gap-2 rounded-full border border-white/45 bg-white/20 px-4 py-2 text-sm font-semibold text-white shadow-[0_10px_28px_rgba(255,255,255,0.16)] backdrop-blur-md transition-all duration-300 group-hover:border-white/70 group-hover:bg-white/30 group-hover:shadow-[0_12px_30px_rgba(255,255,255,0.24)]">
                        Pogledajte
                        <ArrowRight size={20} weight="bold" aria-hidden />
                      </span>
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
}
